import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-hot-toast';

import {
  createExternalSupportingStaffOption,
  employeeSupportingStaffOption,
  externalSupportingStaffOption,
  isValidSupportingStaffEmail
} from './helper/supportingStaff';

const styles = {
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (base) => ({ ...base, minHeight: 40 }),
  menuList: (base) => ({
    ...base,
    maxHeight: 240,
    overflowY: 'auto',
    overscrollBehavior: 'contain'
  })
};

const SupportingStaffSelect = ({ employees, externalContacts, value, onChange, inputId }) => {
  const options = useMemo(() => {
    // eslint-disable-next-line no-undef
    const byValue = new Map();
    [...employees.map(employeeSupportingStaffOption), ...externalContacts.map(externalSupportingStaffOption)].forEach(
      (option) => byValue.set(option.value, option)
    );
    return [...byValue.values()];
  }, [employees, externalContacts]);

  return (
    <CreatableSelect
      inputId={inputId}
      options={options}
      value={value}
      onChange={(selected) => onChange(selected || [])}
      onCreateOption={(inputValue) => {
        if (!isValidSupportingStaffEmail(inputValue)) {
          toast.error('Enter a valid email address');
          return;
        }
        const option = createExternalSupportingStaffOption(inputValue);
        if (!(value || []).some((selected) => selected.value === option.value)) {
          onChange([...(value || []), option]);
        }
      }}
      isValidNewOption={(inputValue, selected, available) =>
        isValidSupportingStaffEmail(inputValue) &&
        ![...selected, ...available].some(
          (option) => option.value === createExternalSupportingStaffOption(inputValue)?.value
        )
      }
      formatCreateLabel={(inputValue) => `Add ${inputValue.trim().toLowerCase()}`}
      placeholder="Select staff or enter an email..."
      isMulti
      isClearable
      isSearchable
      menuPlacement="auto"
      menuPosition="fixed"
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      styles={styles}
    />
  );
};

SupportingStaffSelect.propTypes = {
  employees: PropTypes.array,
  externalContacts: PropTypes.array,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  inputId: PropTypes.string.isRequired
};

SupportingStaffSelect.defaultProps = {
  employees: [],
  externalContacts: [],
  value: []
};

export default SupportingStaffSelect;
